import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import {
  FaArrowDown,
  FaArrowUp,
  FaCheckCircle,
  FaCog,
  FaCopy,
  FaEdit,
  FaList,
  FaPlus,
  FaSync,
  FaToggleOff,
  FaToggleOn,
  FaTrash
} from 'react-icons/fa';
import PubSub from 'pubsub-js';
import jwt_decode from 'jwt-decode';
import WebFormApi from '../../../api/WebFormApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import PicklistApi from '../../../api/PicklistApi';
import LookupLayout from '../../layout/LookupLayout';
import * as constants from '../../../constants/CONSTANT';
import { getEntitlementsSection } from '../../../utils/entitlements';
import './css/WebFormManager.css';

const SYSTEM_FIELDS = new Set([
  'id',
  'createddate',
  'lastmodifieddate',
  'createdbyid',
  'lastmodifiedbyid',
  'ownerid',
  'deleted',
  'isdeleted',
  'created_by',
  'modified_by',
  'lastmodifiedby',
  'createdby',
  'lastactivitydate',
  'systemmodstamp'
]);

const getInitialFormState = () => ({
  form_name: '',
  description: '',
  target_object: 'lead', // Default to lead
  is_active: true,
  default_owner_id: '',
  default_lead_source: '',
  default_lead_status: '',
  generated_html: '',
  styling: {
    button_text: 'Submit',
    success_message: 'Thank you for your submission!'
  },
  settings: {
    redirect_url: '',
    enable_honeypot: true,
    enable_captcha: false
  }
});

const WebFormManager = () => {
  const navigate = useNavigate();
  const webformEnt = getEntitlementsSection('webform', {});
  const allowViewWebform = webformEnt.allowViewWebform !== false;
  const allowCreateWebform = webformEnt.allowCreateWebform !== false;
  const subscriptionObjects = getEntitlementsSection('allowedObjectsPermission', null);
  const rawWebformObjects = webformEnt.allowedObjects;
  const allowedObjectsList = useMemo(() => {
    if (!Array.isArray(rawWebformObjects)) {
      return null;
    }
    if (rawWebformObjects.length > 0) {
      return rawWebformObjects;
    }
    if (Array.isArray(subscriptionObjects) && subscriptionObjects.length > 0) {
      return subscriptionObjects;
    }
    return null;
  }, [rawWebformObjects, subscriptionObjects]);
  const allowedObjectsSet = useMemo(
    () => (allowedObjectsList === null ? null : new Set(allowedObjectsList.map((o) => String(o).toLowerCase()))),
    [allowedObjectsList]
  );

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(getInitialFormState());
  const [objects, setObjects] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [defaultFieldSelections, setDefaultFieldSelections] = useState({
    leadSource: { fieldName: '', value: '' },
    leadStatus: { fieldName: '', value: '' }
  });
  const [embedLoading, setEmbedLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [defaultOwnerLabel, setDefaultOwnerLabel] = useState('');

  const companyId = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return '';
      const decoded = jwt_decode(token);
      return decoded?.companyid || decoded?.companyId || decoded?.company_id || '';
    } catch (err) {
      console.warn('Failed to decode companyId from token', err);
      return '';
    }
  }, []);

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await WebFormApi.listForms({ includeInactive: true, includeDeleted: false });
      const rows = response?.data || response?.rows || response || [];
      setForms(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error loading web forms', error);
      PubSub.publish('RECORD_ERROR_TOAST', {
        title: 'Error',
        message: error.message || 'Failed to load web forms'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadObjects = useCallback(async () => {
    try {
      const response = await ObjectConfigApi.fetchObjectConfigs();
      const rows = response?.data || response?.rows || response || [];
      const allRows = Array.isArray(rows) ? rows.filter(obj => !obj.is_deleted) : [];
      setObjects(allRows);
    } catch (error) {
      console.error('Error loading objects', error);
      PubSub.publish('RECORD_ERROR_TOAST', {
        title: 'Error',
        message: error.message || 'Failed to load objects'
      });
    }
  }, []);

  const visibleObjects = useMemo(() => {
    if (allowedObjectsSet === null) return objects;
    return objects.filter((o) => allowedObjectsSet.has((o.object_name || o.name || '').toLowerCase()));
  }, [objects, allowedObjectsSet]);

  const formsForAllowedObjects = useMemo(() => {
    if (allowedObjectsSet === null) return forms;
    return forms.filter((f) => allowedObjectsSet.has((f.target_object || '').toLowerCase()));
  }, [forms, allowedObjectsSet]);

  useEffect(() => {
    if (!allowViewWebform) {
      navigate('/setup', { replace: true });
      return;
    }
    loadForms();
    loadObjects();
  }, [allowViewWebform, navigate, loadForms, loadObjects]);

  const getFieldLabel = (field) =>
    field?.field_label ||
    field?.fieldLabel ||
    field?.label ||
    field?.fieldname ||
    field?.field_name ||
    '';

  const formatPicklistOption = (option) => {
    if (option === undefined || option === null) {
      return null;
    }

    if (typeof option === 'string') {
      return { value: option, label: option };
    }

    if (typeof option === 'object') {
      const value =
        option.value_key ??
        option.key ??
        option.value ??
        option.valueKey ??
        option.value_label ??
        option.label ??
        '';

      const label =
        option.label ??
        option.value_label ??
        option.valueLabel ??
        option.value ??
        option.value_key ??
        value;

      return value ? { value, label } : null;
    }

    return null;
  };

  const parsePicklistValues = (rawValues) => {
    if (!rawValues) return [];

    if (Array.isArray(rawValues)) {
      return rawValues.map(formatPicklistOption).filter(Boolean);
    }

    if (typeof rawValues === 'string') {
      try {
        const parsed = JSON.parse(rawValues.replace(/\\n/g, ''));
        if (Array.isArray(parsed)) {
          return parsed.map(formatPicklistOption).filter(Boolean);
        }
      } catch (error) {
        // Silent - fall through
      }

      return rawValues
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
        .map((value) => ({ value, label: value }));
    }

    if (typeof rawValues === 'object') {
      if (rawValues instanceof Map) {
        return Array.from(rawValues.entries())
          .map(([value, label]) => ({ value, label: label || value }))
          .map(formatPicklistOption)
          .filter(Boolean);
      }

      return Object.entries(rawValues)
        .map(([value, label]) => ({ value, label: label || value }))
        .map(formatPicklistOption)
        .filter(Boolean);
    }

    return [];
  };

  const normalizeField = (field) => {
    const picklistSource =
      field.picklist_values ??
      field.picklistValues ??
      field.picklist_values_data ??
      field.picklistValuesData ??
      field.picklist_values_json ??
      field.picklistValuesJson ??
      field.picklist_values_data_json ??
      field.picklistValuesDataJson ??
      null;

    return {
      id: field.id || field.field_id || null,
      field_name: field.field_name || field.fieldName,
      field_label: getFieldLabel(field),
      is_required: Boolean(field.is_required ?? field.isRequired),
      is_readonly: Boolean(field.is_readonly ?? field.isReadonly),
      data_type: (field.data_type || field.dataType || '').toLowerCase(),
      default_value: field.default_value || '',
      display_order: field.display_order || field.displayOrder || null,
      picklist_values: parsePicklistValues(picklistSource),
      reference_object: field.reference_object || field.referenceObject || null
    };
  };

  const filterFields = (fields) =>
    fields
      .map(normalizeField)
      .filter(
        (field) =>
          field.field_name &&
          !field.is_readonly &&
          !SYSTEM_FIELDS.has(field.field_name.toLowerCase())
      );

  const loadObjectFields = useCallback(
    async (objectName, preselected = [], enforceRequired = true) => {
      if (!objectName) {
        setFieldOptions([]);
        setSelectedFields([]);
        return;
      }
      setFieldsLoading(true);
      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
        const payloadFields =
          response?.fields ||
          response?.data?.fields ||
          response?.objectFields ||
          response?.object_config_fields ||
          [];

        const filtered = filterFields(Array.isArray(payloadFields) ? payloadFields : []);

        const picklistFieldsToFetch = filtered.filter(
          (field) =>
            (field.data_type === 'picklist' || field.data_type === 'multipicklist') &&
            field.id &&
            (!field.picklist_values || field.picklist_values.length === 0)
        );

        if (picklistFieldsToFetch.length > 0) {
          const picklistApi = new PicklistApi();
          await Promise.all(
            picklistFieldsToFetch.map(async (field) => {
              try {
                const response = await picklistApi.getActivePicklistValues(field.id);
                const payload = response?.data;
                const values = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
                if (Array.isArray(values) && values.length > 0) {
                  field.picklist_values = values
                    .map((item) =>
                      formatPicklistOption({
                        value_key:
                          item.value_key ??
                          item.valueKey ??
                          item.key ??
                          item.value ??
                          '',
                        value_label:
                          item.value_label ??
                          item.valueLabel ??
                          item.label ??
                          item.value ??
                          item.value_key ??
                          ''
                      })
                    )
                    .filter(Boolean);
                }
              } catch (error) {
                console.warn(
                  `Failed to load picklist values for field ${field.field_name}:`,
                  error
                );
              }
            })
          );
        }

        const requiredFields = filtered.filter((field) => field.is_required);

        const preselectedMap = new Map(
          (Array.isArray(preselected) ? preselected : []).map((item) => [
            item.field_name,
            {
              ...item,
              display_order: item.display_order || item.displayOrder || null
            }
          ])
        );

        const selected = [];

        if (preselectedMap.size > 0) {
          filtered.forEach((field) => {
            const existing = preselectedMap.get(field.field_name);
            if (existing) {
              let defaultValue = existing.default_value ?? existing.defaultValue ?? '';
              if (field.data_type === 'multipicklist') {
                if (Array.isArray(defaultValue)) {
                  defaultValue = defaultValue;
                } else if (typeof defaultValue === 'string' && defaultValue.length > 0) {
                  defaultValue = defaultValue.split(';').map((item) => item.trim()).filter(Boolean);
                } else {
                  defaultValue = [];
                }
              }
              selected.push({
                ...field,
                default_value: defaultValue || '',
                is_required: field.is_required || existing.is_required,
                display_order: existing.display_order
              });
            }
          });
        }

        if (enforceRequired) {
          requiredFields.forEach((field) => {
            if (!selected.find(item => item.field_name === field.field_name)) {
              selected.push({ ...field });
            }
          });
        }

        const orderedSelected = selected
          .map((field, index) => ({
            ...field,
            display_order: field.display_order ?? index + 1
          }))
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
          .map((field, index) => ({ ...field, display_order: index + 1 }));

        setFieldOptions(filtered);
        setSelectedFields(orderedSelected);

        setDefaultFieldSelections((prev) => {
          const adjustSelection = (selection) => {
            if (!selection.fieldName) {
              return { fieldName: '', value: '' };
            }

            const field = filtered.find((item) => item.field_name === selection.fieldName);
            if (!field) {
              return { fieldName: '', value: '' };
            }

            const hasValue = (field.picklist_values || []).some((optionRaw) => {
              const option = normalizeOption(optionRaw);
              return option?.value === selection.value;
            });

            return {
              fieldName: selection.fieldName,
              value: hasValue ? selection.value : ''
            };
          };

          return {
            leadSource: adjustSelection(prev.leadSource),
            leadStatus: adjustSelection(prev.leadStatus)
          };
        });
      } catch (error) {
        console.error('Error loading object fields', error);
        PubSub.publish('RECORD_ERROR_TOAST', {
          title: 'Error',
          message: error.message || 'Failed to load fields for selected object'
        });
        setFieldOptions([]);
        setSelectedFields([]);
      } finally {
        setFieldsLoading(false);
      }
    },
    []
  );

  const handleCreateForm = () => {
    setFormData(getInitialFormState());
    setSelectedFields([]);
    setFieldOptions([]);
    setSelectedForm(null);
    setDefaultOwnerLabel('');
    setDefaultFieldSelections({
      leadSource: { fieldName: '', value: '' },
      leadStatus: { fieldName: '', value: '' }
    });
    setIsEditing(false);
    setShowModal(true);

    // Automatically load fields for the default 'lead' object
    loadObjectFields('lead', [], true);
  };

  const openEditModal = (form) => {
    if (!form) return;
    setSelectedForm(form);
    const config =
      typeof form.form_config === 'string'
        ? JSON.parse(form.form_config || '{}')
        : form.form_config || {};

    setFormData({
      form_name: form.form_name || '',
      description: form.description || '',
      target_object: form.target_object || '',
      is_active: Boolean(form.is_active),
      default_owner_id: form.default_owner_id || '',
      default_lead_source: form.default_lead_source || '',
      default_lead_status: form.default_lead_status || '',
      generated_html: form.generated_html || '',
      styling: {
        button_text: config?.styling?.button_text || 'Submit',
        success_message: config?.styling?.success_message || 'Thank you for your submission!'
      },
      settings: {
        redirect_url: config?.settings?.redirect_url || '',
        enable_honeypot:
          config?.settings?.enable_honeypot === undefined
            ? true
            : Boolean(config.settings.enable_honeypot),
        enable_captcha: Boolean(config?.settings?.enable_captcha)
      }
    });

    const preselectedFields = Array.isArray(config?.fields) ? config.fields : [];

    const defaultSelections = config?.settings?.defaultFieldSelections || {};
    setDefaultFieldSelections({
      leadSource: {
        fieldName: defaultSelections?.leadSource?.fieldName || '',
        value: defaultSelections?.leadSource?.value || ''
      },
      leadStatus: {
        fieldName: defaultSelections?.leadStatus?.fieldName || '',
        value: defaultSelections?.leadStatus?.value || ''
      }
    });

    // Set default owner label if available
    setDefaultOwnerLabel(config?.settings?.defaultOwnerLabel || '');

    setIsEditing(true);
    setShowModal(true);
    loadObjectFields(form.target_object, preselectedFields, false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSaving(false);
    setSelectedForm(null);
  };

  const handleFormInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name.startsWith('styling.')) {
      const key = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        styling: {
          ...prev.styling,
          [key]: value
        }
      }));
    } else if (name.startsWith('settings.')) {
      const key = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          [key]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleTargetObjectChange = async (event) => {
    const newTargetObject = event.target.value;
    setFormData((prev) => ({
      ...prev,
      target_object: newTargetObject
    }));
    setSelectedFields([]);
    setFieldOptions([]);
    setDefaultFieldSelections({
      leadSource: { fieldName: '', value: '' },
      leadStatus: { fieldName: '', value: '' }
    });
    if (newTargetObject) {
      await loadObjectFields(
        newTargetObject,
        isEditing ? selectedFields : [],
        !isEditing
      );
    }
  };

  const availableOptionalFields = useMemo(() => {
    const selectedNames = new Set(selectedFields.map((field) => field.field_name));
    return fieldOptions.filter((field) => !selectedNames.has(field.field_name));
  }, [fieldOptions, selectedFields]);

  const picklistFieldOptions = useMemo(
    () => fieldOptions.filter((field) => field.data_type === 'picklist'),
    [fieldOptions]
  );

  const currentObject = useMemo(
    () =>
      objects.find((object) => object.object_name === formData.target_object) || null,
    [objects, formData.target_object]
  );

  const currentObjectLabel = useMemo(() => {
    if (!currentObject) return 'Record';
    return (
      currentObject.object_label ||
      currentObject.label ||
      currentObject.objectName ||
      currentObject.object_name ||
      'Record'
    );
  }, [currentObject]);

  const updateSelectedFields = (nextFields) => {
    setSelectedFields(
      nextFields.map((field, index) => ({
        ...field,
        display_order: index + 1
      }))
    );
  };

  const handleAddField = (field) => {
    if (!field) return;
    updateSelectedFields([...selectedFields, { ...field }]);
  };

  const handleRemoveField = (fieldName) => {
    const field = selectedFields.find((item) => item.field_name === fieldName);
    if (!field) {
      return;
    }

    const isDefaultSelected =
      defaultFieldSelections.leadSource.fieldName === fieldName ||
      defaultFieldSelections.leadStatus.fieldName === fieldName;

    if (field.is_required && !isDefaultSelected) {
      return;
    }

    updateSelectedFields(selectedFields.filter((item) => item.field_name !== fieldName));
  };

  const handleMoveField = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= selectedFields.length) return;
    const updated = [...selectedFields];
    const temp = updated[index];
    updated[index] = updated[newIndex];
    updated[newIndex] = temp;
    updateSelectedFields(updated);
  };

  const normalizeOption = (option) => {
    if (!option) return null;
    if (typeof option === 'string') {
      return { value: option, label: option };
    }
    if (typeof option === 'object') {
      return {
        value: option.value ?? option.value_key ?? option.key ?? option.label ?? option,
        label:
          option.label ??
          option.value_label ??
          option.value ??
          option.key ??
          option.value_key ??
          ''
      };
    }
    return null;
  };

  const handleDefaultValueChange = (index, value) => {
    const updated = [...selectedFields];
    updated[index] = {
      ...updated[index],
      default_value: value
    };
    setSelectedFields(updated);
  };

  const handleDefaultFieldSelectionChange = (key, updates) => {
    setDefaultFieldSelections(prev => {
      const next = { ...prev };
      const previousFieldName = prev[key].fieldName;
      const nextFieldName =
        updates.fieldName !== undefined ? updates.fieldName : prev[key].fieldName;
      const nextValue =
        updates.value !== undefined ? updates.value : prev[key].value;

      next[key] = {
        fieldName: nextFieldName,
        value: nextValue
      };

      if (
        updates.fieldName !== undefined &&
        !nextFieldName &&
        previousFieldName
      ) {
        const requiredField = fieldOptions.find(
          field => field.field_name === previousFieldName && field.is_required
        );
        const existsInSelection = selectedFields.some(
          field => field.field_name === previousFieldName
        );
        if (requiredField && !existsInSelection) {
          updateSelectedFields([...selectedFields, { ...requiredField }]);
        }
      }

      return next;
    });
  };

  const renderDefaultFieldSelector = (
    label,
    selectionKey,
    picklistFieldOptionsArg,
    defaultFieldSelectionsState
  ) => {
    const selection = defaultFieldSelectionsState[selectionKey];
    const selectedField = picklistFieldOptionsArg.find(
      (field) => field.field_name === selection.fieldName
    );
    const options = selectedField ? selectedField.picklist_values : [];

    return (
      <div className='default-field-selector mb-2'>
        <Form.Label>{label}</Form.Label>
        <Row className='default-selector-row align-items-center'>
          <Col xs='auto' className='mb-2 field-select'>
            <Form.Select
              value={selection.fieldName}
              onChange={(event) => {
                handleDefaultFieldSelectionChange(selectionKey, {
                  fieldName: event.target.value,
                  value: ''
                });
              }}
            >
              <option value=''>-- Select picklist field --</option>
              {picklistFieldOptionsArg.map((field) => (
                <option key={field.field_name} value={field.field_name}>
                  {field.field_label}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col className='mb-2 value-select'>
            <Form.Select
              value={selection.value}
              onChange={(event) =>
                handleDefaultFieldSelectionChange(selectionKey, { value: event.target.value })
              }
              disabled={!selection.fieldName}
            >
              <option value=''>-- Select value --</option>
              {options.map((optionRaw, index) => {
                const option = normalizeOption(optionRaw);
                if (!option) return null;
                return (
                  <option key={`${selectionKey}_${index}`} value={option.value}>
                    {option.label || option.value}
                  </option>
                );
              })}
            </Form.Select>
          </Col>
        </Row>
      </div>
    );
  };

  const handleFetchEmbedHtml = async (regenerate = false) => {
    if (!selectedForm?.id) {
      PubSub.publish('RECORD_ERROR_TOAST', {
        title: 'Unavailable',
        message: 'Save the form first to generate embed HTML.'
      });
      return;
    }

    try {
      setEmbedLoading(true);
      console.log('Fetching embed HTML for form:', selectedForm.id, 'regenerate:', regenerate);

      // If regenerating, save current configuration first to ensure HTML matches UI state
      if (regenerate) {
        console.log('Regenerate requested - saving form configuration first...');
        try {
          const payload = buildFormPayload();
          await WebFormApi.updateForm(selectedForm.id, payload);
          console.log('Form configuration saved, now generating HTML...');
        } catch (saveError) {
          console.error('Error saving form before regenerating HTML:', saveError);
          PubSub.publish('RECORD_ERROR_TOAST', {
            title: 'Error',
            message: 'Failed to save form configuration: ' + (saveError.message || 'Unknown error')
          });
          return null;
        }
      }

      const response = await WebFormApi.getEmbedHtml(selectedForm.id, {
        regenerate,
        includeStyles: true,
        includeScript: true
      });

      console.log('Embed HTML Response:', response);
      const html = response?.data?.html || '';
      console.log('Extracted HTML length:', html.length);
      console.log('HTML preview:', html.substring(0, 200));

      setFormData((prev) => {
        const updated = {
          ...prev,
          generated_html: html
        };
        console.log('Updated formData with new HTML');
        return updated;
      });

      PubSub.publish('RECORD_SAVED_TOAST', {
        title: regenerate ? 'Regenerated' : 'Loaded',
        message: regenerate
          ? 'Form saved and HTML regenerated successfully.'
          : 'Loaded stored embed HTML.'
      });

      return html;
    } catch (error) {
      console.error('Error fetching embed HTML', error);
      PubSub.publish('RECORD_ERROR_TOAST', {
        title: 'Error',
        message: error.message || 'Failed to load embed HTML'
      });
      return null;
    } finally {
      setEmbedLoading(false);
    }
  };

  const buildFormPayload = () => {
    const trimmedName = formData.form_name?.trim();
    if (!trimmedName) {
      throw new Error('Form name is required');
    }
    if (!formData.target_object) {
      throw new Error('Target object is required');
    }
    if (selectedFields.length === 0) {
      throw new Error('Select at least one field');
    }
    if (!formData.default_owner_id) {
      throw new Error('Default owner is required');
    }
    const trimmedRedirectUrl = formData.settings.redirect_url?.trim();
    if (!trimmedRedirectUrl || trimmedRedirectUrl === 'http://') {
      throw new Error('Redirect URL is required');
    }
    const trimmedButtonText = formData.styling.button_text?.trim();
    if (!trimmedButtonText) {
      throw new Error('Submit button text is required');
    }
    const trimmedSuccessMessage = formData.styling.success_message?.trim();
    if (!trimmedSuccessMessage) {
      throw new Error('Success message is required');
    }

    const fieldsPayload = selectedFields.map((field, index) => {
      const normalizedPicklistValues = Array.isArray(field.picklist_values)
        ? field.picklist_values
          .map((option) => {
            const normalized = normalizeOption(option);
            return normalized ? { value: normalized.value, label: normalized.label || normalized.value } : null;
          })
          .filter(Boolean)
        : [];

      return {
        field_name: field.field_name,
        field_label: field.field_label,
        data_type: field.data_type,
        is_required: Boolean(field.is_required),
        display_order: index + 1,
        default_value: Array.isArray(field.default_value)
          ? field.default_value.join(';')
          : field.default_value || '',
        picklist_values: normalizedPicklistValues
      };
    });

    return {
      form_name: trimmedName,
      description: formData.description?.trim() || '',
      target_object: formData.target_object,
      is_active: Boolean(formData.is_active),
      default_owner_id: formData.default_owner_id || null,
      default_lead_source: defaultFieldSelections.leadSource.value || null,
      default_lead_status: defaultFieldSelections.leadStatus.value || null,
      generated_html: formData.generated_html || null,
      form_config: {
        fields: fieldsPayload,
        styling: {
          button_text: formData.styling.button_text || 'Submit',
          success_message:
            formData.styling.success_message || 'Thank you for your submission!'
        },
        settings: {
          redirect_url: formData.settings.redirect_url || '',
          enable_honeypot: Boolean(formData.settings.enable_honeypot),
          enable_captcha: Boolean(formData.settings.enable_captcha),
          defaultFieldSelections,
          defaultOwnerLabel
        }
      }
    };
  };

  const handleSaveForm = async () => {
    const wasEditingExisting = Boolean(isEditing && selectedForm?.id);
    try {
      setSaving(true);
      const payload = buildFormPayload();
      console.log('Payload:', payload);

      let savedForm;
      let embedHtml = '';
      if (wasEditingExisting) {
        // Update existing form
        const updateResponse = await WebFormApi.updateForm(selectedForm.id, payload);
        savedForm = updateResponse?.data || updateResponse;

        // Generate updated HTML with the current configuration
        const htmlResponse = await WebFormApi.getEmbedHtml(selectedForm.id, {
          regenerate: true,
          includeStyles: true,
          includeScript: true
        });
        embedHtml = htmlResponse?.data?.html || '';

        PubSub.publish('RECORD_SAVED_TOAST', {
          title: 'Success',
          message: 'Web form updated successfully'
        });
      } else {
        // Create new form
        const createResponse = await WebFormApi.createForm(payload);
        savedForm = createResponse?.data || createResponse;

        // Generate HTML for the newly created form
        if (savedForm?.id) {
          const htmlResponse = await WebFormApi.getEmbedHtml(savedForm.id, {
            regenerate: true,
            includeStyles: true,
            includeScript: true
          });
          embedHtml = htmlResponse?.data?.html || '';
        }

        PubSub.publish('RECORD_SAVED_TOAST', {
          title: 'Success',
          message: 'Web form created successfully. Embed HTML is ready below.'
        });
      }

      if (embedHtml) {
        setFormData((prev) => ({ ...prev, generated_html: embedHtml }));
      }
      if (savedForm?.id) {
        setSelectedForm(savedForm);
        setIsEditing(true);
      }

      loadForms();
      if (wasEditingExisting) {
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error saving web form', error);
      PubSub.publish('RECORD_ERROR_TOAST', {
        title: 'Error',
        message: error.message || 'Failed to save web form'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteForm = async (form) => {
    if (!form) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete the web form "${form.form_name}"?`
    );
    if (!confirmed) return;
    try {
      await WebFormApi.deleteForm(form.id);
      PubSub.publish('RECORD_SAVED_TOAST', {
        title: 'Success',
        message: 'Web form deleted successfully'
      });
      loadForms();
    } catch (error) {
      console.error('Error deleting web form', error);
      PubSub.publish('RECORD_ERROR_TOAST', {
        title: 'Error',
        message: error.message || 'Failed to delete web form'
      });
    }
  };

  const handleToggleStatus = async (form) => {
    if (!form) return;
    try {
      await WebFormApi.toggleFormStatus(form.id, !form.is_active);
      PubSub.publish('RECORD_SAVED_TOAST', {
        title: 'Success',
        message: `Form marked as ${!form.is_active ? 'active' : 'inactive'}`
      });
      loadForms();
    } catch (error) {
      console.error('Error toggling form status', error);
      PubSub.publish('RECORD_ERROR_TOAST', {
        title: 'Error',
        message: error.message || 'Failed to update form status'
      });
    }
  };

  const handleRegenerateToken = async (form) => {
    if (!form) return;
    const confirmed = window.confirm(
      `Regenerating the token will invalidate existing embeds for "${form.form_name}". Continue?`
    );
    if (!confirmed) return;
    try {
      await WebFormApi.regenerateToken(form.id);
      PubSub.publish('RECORD_SAVED_TOAST', {
        title: 'Success',
        message: 'Token regenerated successfully'
      });
      loadForms();
    } catch (error) {
      console.error('Error regenerating token', error);
      PubSub.publish('RECORD_ERROR_TOAST', {
        title: 'Error',
        message: error.message || 'Failed to regenerate token'
      });
    }
  };

  const handleCopySubmissionUrl = async (form) => {
    if (!navigator?.clipboard) {
      return;
    }
    const token = form?.form_token;
    if (!token) {
      PubSub.publish('RECORD_ERROR_TOAST', {
        title: 'Error',
        message: 'Token not available for this form'
      });
      return;
    }
    const submissionUrl = companyId
      ? `${constants.API_BASE_URL}/api/web-object/submit/${companyId}/${token}`
      : `${constants.API_BASE_URL}/api/web-object/submit/<company-id>/${token}`;
    try {
      await navigator.clipboard.writeText(submissionUrl);
      PubSub.publish('RECORD_SAVED_TOAST', {
        title: 'Copied',
        message: 'Submission URL copied to clipboard'
      });
    } catch (error) {
      console.error('Failed to copy submission URL', error);
      PubSub.publish('RECORD_ERROR_TOAST', {
        title: 'Error',
        message: 'Failed to copy submission URL'
      });
    }
  };

  const renderFormRows = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan={7} className='text-center py-4'>
            <Spinner animation='border' variant='primary' />
          </td>
        </tr>
      );
    }

    if (!formsForAllowedObjects.length) {
      return (
        <tr>
          <td colSpan={7} className='text-center py-4 text-muted'>
            {allowCreateWebform ? 'No web forms found. Click "New Form" to create one.' : 'No web forms found.'}
          </td>
        </tr>
      );
    }

    return formsForAllowedObjects.map((form) => (
      <tr key={form.id}>
        <td>
          <div className='d-flex flex-column'>
            <strong>{form.form_name}</strong>
            {form.description && (
              <span className='text-muted small'>{form.description}</span>
            )}
          </div>
        </td>
        <td>{form.target_object}</td>
        <td>
          {form.is_active ? (
            <Badge bg='success'>Active</Badge>
          ) : (
            <Badge bg='secondary'>Inactive</Badge>
          )}
        </td>
        <td className='text-center'>{form.submission_count || 0}</td>
        <td>
          {form.last_submission_date
            ? new Date(form.last_submission_date).toLocaleString()
            : '—'}
        </td>
        <td>
          <div className='d-flex align-items-center gap-2'>
            <code className='web-form-token'>{form.form_token}</code>
            <Button
              variant='light'
              size='sm'
              onClick={() => handleCopySubmissionUrl(form)}
              title='Copy submission URL'
            >
              <FaCopy />
            </Button>
          </div>
        </td>
        <td>
          <div className='d-flex gap-2 flex-wrap'>
            {allowCreateWebform && (
              <>
                <Button variant='outline-primary' size='sm' onClick={() => openEditModal(form)}>
                  <FaEdit /> Edit
                </Button>
                <Button
                  variant={form.is_active ? 'outline-warning' : 'outline-success'}
                  size='sm'
                  onClick={() => handleToggleStatus(form)}
                >
                  {form.is_active ? (
                    <>
                      <FaToggleOff /> Deactivate
                    </>
                  ) : (
                    <>
                      <FaToggleOn /> Activate
                    </>
                  )}
                </Button>
                <Button
                  variant='outline-info'
                  size='sm'
                  onClick={() => handleRegenerateToken(form)}
                >
                  <FaSync /> Token
                </Button>
                <Button
                  variant='outline-danger'
                  size='sm'
                  onClick={() => handleDeleteForm(form)}
                >
                  <FaTrash /> Delete
                </Button>
              </>
            )}
          </div>
        </td>
      </tr>
    ));
  };

  if (!allowViewWebform) return null;

  return (
    <div className='web-form-manager-wrapper'>
      <Card className='mb-4'>
        <Card.Body className='d-flex justify-content-between align-items-center'>
          <div>
            <Card.Title className='mb-0'>Web Forms</Card.Title>
            <Card.Text className='text-muted'>
              Create embeddable forms for any configured object.
            </Card.Text>
          </div>
          {allowCreateWebform && (
            <Button variant='primary' onClick={handleCreateForm}>
              <FaPlus className='me-2' />
              New Form
            </Button>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Table responsive hover className='align-middle'>
            <thead>
              <tr>
                <th>Name</th>
                <th>Object</th>
                <th>Status</th>
                <th className='text-center'>Submissions</th>
                <th>Last Submission</th>
                <th>Token</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>{renderFormRows()}</tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size='lg'
        backdrop='static'
        centered
        dialogClassName='web-form-editor-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit Web Form' : 'New Web Form'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Form Configuration Section - Full Width */}
            <Card className='mb-3'>
              <Card.Header className='layout-card-header'>
                <h6 className='mb-0'>
                  <FaCog className='me-2' size={16} />
                  Form Configuration
                </h6>
              </Card.Header>
              <Card.Body>
                <Row className='mb-3'>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Form Name</Form.Label>
                      <Form.Control
                        type='text'
                        name='form_name'
                        placeholder='Enter form name'
                        value={formData.form_name}
                        onChange={handleFormInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Target Object</Form.Label>
                      <Form.Select
                        name='target_object'
                        value={formData.target_object}
                        onChange={handleTargetObjectChange}
                        disabled={isEditing}
                      >
                        {visibleObjects.map((object) => (
                          <option key={object.object_name || object.id} value={object.object_name}>
                            {object.object_label || object.label || object.object_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='mb-3'>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as='textarea'
                        rows={2}
                        name='description'
                        placeholder='Optional description to help admins identify this form'
                        value={formData.description}
                        onChange={handleFormInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='mb-3'>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>
                        Default Owner <span className='text-danger'>*</span>
                      </Form.Label>
                      <LookupLayout
                        objectName='user'
                        searchFields='firstname,lastname'
                        displayFields='firstname,lastname'
                        value={formData.default_owner_id || ''}
                        defaultLabel={defaultOwnerLabel}
                        onChange={(selectedUser) => {
                          console.log('Default Owner Selected:', selectedUser);
                          let ownerId = null;
                          let ownerLabel = '';

                          if (typeof selectedUser === 'string') {
                            // If LookupLayout passes UUID directly as string
                            ownerId = selectedUser;
                            // We don't have the label here, it will be loaded on edit
                          } else if (selectedUser && typeof selectedUser === 'object') {
                            // If LookupLayout passes an object with id and other properties
                            ownerId = selectedUser.id || null;
                            ownerLabel = selectedUser
                              ? `${selectedUser.firstname || ''} ${selectedUser.lastname || ''}`.trim() || selectedUser.email || ''
                              : '';
                          }

                          console.log('Setting default_owner_id to:', ownerId);
                          setFormData(prev => ({
                            ...prev,
                            default_owner_id: ownerId
                          }));
                          setDefaultOwnerLabel(ownerLabel);
                        }}
                        placeholder='Search for user...'
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='mb-3'>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Check
                        type='switch'
                        name='is_active'
                        label='Active'
                        checked={formData.is_active}
                        onChange={handleFormInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    {picklistFieldOptions.length > 0 ? (
                      <Row className='g-3'>
                        <Col md={6} sm={12}>
                          {renderDefaultFieldSelector(
                            `Default ${currentObjectLabel} Source`,
                            'leadSource',
                            picklistFieldOptions,
                            defaultFieldSelections
                          )}
                        </Col>
                        <Col md={6} sm={12}>
                          {renderDefaultFieldSelector(
                            `Default ${currentObjectLabel} Status`,
                            'leadStatus',
                            picklistFieldOptions,
                            defaultFieldSelections
                          )}
                        </Col>
                      </Row>
                    ) : (
                      <div className='text-muted small mt-2'>
                        Select an object with picklist fields to configure default values.
                      </div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Fields Section - Two Column Layout */}
            <Row>
              {/* Left Column - Available Fields */}
              <Col md={4}>
                <Card>
                  <Card.Header className='layout-card-header'>
                    <h6 className='mb-0'>
                      <FaPlus className='me-2' />
                      Available Optional Fields
                      <Badge bg='light' text='dark' className='ms-2'>
                        {availableOptionalFields.length}
                      </Badge>
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {fieldsLoading ? (
                      <div className='text-center py-3'>
                        <Spinner animation='border' variant='primary' size='sm' />
                        <p className='text-muted small mt-2'>Loading fields...</p>
                      </div>
                    ) : availableOptionalFields.length === 0 ? (
                      <div className='text-center text-muted py-4'>
                        <FaCheckCircle className='text-success mb-2' size={24} />
                        <div className='small'>All fields have been added</div>
                      </div>
                    ) : (
                      <ListGroup variant='flush'>
                        {availableOptionalFields.map((field) => (
                          <ListGroup.Item
                            key={field.field_name}
                            className='d-flex justify-content-between align-items-center p-2'
                            style={{ cursor: 'pointer', border: '1px solid #dee2e6', marginBottom: '8px', borderRadius: '4px' }}
                            onClick={() => handleAddField(field)}
                          >
                            <div>
                              <div className='fw-bold text-primary'>{field.field_label}</div>
                              <small className='text-muted'>{field.field_name}</small>
                            </div>
                            <div className='d-flex align-items-center'>
                              <Badge bg='secondary' className='me-2'>{field.data_type}</Badge>
                              <Button variant='outline-primary' size='sm' onClick={() => handleAddField(field)}>
                                <FaPlus size={10} />
                              </Button>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Right Column - Selected Fields */}
              <Col md={8}>
                <Card>
                  <Card.Header className='layout-card-header'>
                    <h6 className='mb-0'>
                      <FaList className='me-2' />
                      Form Fields
                      <Badge bg='light' text='dark' className='ms-2'>
                        {selectedFields.length}
                      </Badge>
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {fieldsLoading ? (
                      <div className='text-center py-3'>
                        <Spinner animation='border' variant='primary' />
                      </div>
                    ) : selectedFields.length === 0 ? (
                      <div className='text-center text-muted py-5'>
                        <FaList size={64} className='mb-3 text-muted' />
                        <h6>No Fields Selected</h6>
                        <p className='mb-0'>Select an object and add fields from the left panel</p>
                      </div>
                    ) : (
                      <div className='selected-fields-list'>
                        {selectedFields.map((field, index) => (
                          <Card key={field.field_name} className='mb-2'>
                            <Card.Body className='d-flex justify-content-between align-items-start gap-3'>
                              <div className='flex-grow-1'>
                                <div className='d-flex align-items-center flex-wrap gap-2 mb-2'>
                                  <strong>{field.field_label}</strong>
                                  <Badge bg='light' text='dark'>
                                    {field.field_name}
                                  </Badge>
                                  <Form.Check
                                    type='switch'
                                    id={`webform-field-required-${field.field_name}`}
                                    className='ms-md-auto'
                                    label='Required on form'
                                    checked={Boolean(field.is_required)}
                                    onChange={() => {
                                      const updated = [...selectedFields];
                                      updated[index] = {
                                        ...updated[index],
                                        is_required: !updated[index].is_required
                                      };
                                      updateSelectedFields(updated);
                                    }}
                                  />
                                </div>
                                <Row>
                                  <Col md={field.data_type === 'multipicklist' ? 12 : 6}>
                                    <Form.Group controlId={`default_${field.field_name}`}>
                                      <Form.Label className='small text-muted'>
                                        Default Value (optional)
                                      </Form.Label>
                                      {field.data_type === 'picklist' ? (
                                        <Form.Select
                                          size='sm'
                                          value={field.default_value || ''}
                                          onChange={(event) =>
                                            handleDefaultValueChange(index, event.target.value)
                                          }
                                        >
                                          <option value=''>-- Select value --</option>
                                          {field.picklist_values.map((optionRaw, optionIndex) => {
                                            const option = normalizeOption(optionRaw);
                                            if (!option) return null;
                                            return (
                                              <option key={`${field.field_name}_${optionIndex}`} value={option.value}>
                                                {option.label || option.value}
                                              </option>
                                            );
                                          })}
                                        </Form.Select>
                                      ) : field.data_type === 'multipicklist' ? (
                                        <Form.Select
                                          size='sm'
                                          multiple
                                          value={
                                            Array.isArray(field.default_value)
                                              ? field.default_value
                                              : (field.default_value || '')
                                                .split(';')
                                                .map((item) => item.trim())
                                                .filter(Boolean)
                                          }
                                          onChange={(event) => {
                                            const selected = Array.from(
                                              event.target.selectedOptions,
                                              (option) => option.value
                                            );
                                            handleDefaultValueChange(index, selected);
                                          }}
                                        >
                                          {field.picklist_values.map((optionRaw, optionIndex) => {
                                            const option = normalizeOption(optionRaw);
                                            if (!option) return null;
                                            return (
                                              <option key={`${field.field_name}_${optionIndex}`} value={option.value}>
                                                {option.label || option.value}
                                              </option>
                                            );
                                          })}
                                        </Form.Select>
                                      ) : (
                                        <Form.Control
                                          size='sm'
                                          type='text'
                                          value={field.default_value || ''}
                                          onChange={(event) =>
                                            handleDefaultValueChange(index, event.target.value)
                                          }
                                          placeholder='Enter default value'
                                        />
                                      )}
                                    </Form.Group>
                                  </Col>
                                </Row>
                              </div>
                              <div className='d-flex flex-column gap-2 align-items-end'>
                                <div className='btn-group'>
                                  <Button
                                    variant='light'
                                    size='sm'
                                    onClick={() => handleMoveField(index, -1)}
                                    disabled={index === 0}
                                  >
                                    <FaArrowUp />
                                  </Button>
                                  <Button
                                    variant='light'
                                    size='sm'
                                    onClick={() => handleMoveField(index, 1)}
                                    disabled={index === selectedFields.length - 1}
                                  >
                                    <FaArrowDown />
                                  </Button>
                                </div>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  disabled={
                                    field.is_required &&
                                    !(
                                      defaultFieldSelections.leadSource.fieldName === field.field_name ||
                                      defaultFieldSelections.leadStatus.fieldName === field.field_name
                                    )
                                  }
                                  onClick={() => handleRemoveField(field.field_name)}
                                >
                                  <FaTrash /> Remove
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Submission Settings Section - Full Width */}
            <Card className='mt-3'>
              <Card.Header className='layout-card-header'>
                <h6 className='mb-0'>
                  <FaCog className='me-2' size={16} />
                  Submission Settings
                </h6>
              </Card.Header>
              <Card.Body>
                <Row className='mb-3'>
                  <Col md={6}>
                    <Form.Group controlId='styling.button_text'>
                      <Form.Label>
                        Submit Button Text <span className='text-danger'>*</span>
                      </Form.Label>
                      <Form.Control
                        type='text'
                        name='styling.button_text'
                        value={formData.styling.button_text}
                        onChange={handleFormInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId='settings.redirect_url'>
                      <Form.Label>
                        Redirect URL <span className='text-danger'>*</span>
                      </Form.Label>
                      <Form.Control
                        type='text'
                        name='settings.redirect_url'
                        placeholder='https://example.com/thank-you'
                        value={formData.settings.redirect_url}
                        onChange={handleFormInputChange}
                        required
                      />
                      <Form.Text className='text-muted'>
                        Enter a valid URL where users will be redirected after form submission
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group controlId='styling.success_message' className='mb-3'>
                  <Form.Label>
                    Success Message <span className='text-danger'>*</span>
                  </Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={2}
                    name='styling.success_message'
                    value={formData.styling.success_message}
                    onChange={handleFormInputChange}
                    required
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Check
                      type='switch'
                      name='settings.enable_honeypot'
                      id='settings.enable_honeypot'
                      label='Enable honeypot spam protection'
                      checked={formData.settings.enable_honeypot}
                      onChange={handleFormInputChange}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type='switch'
                      name='settings.enable_captcha'
                      id='settings.enable_captcha'
                      label='Enable CAPTCHA'
                      checked={formData.settings.enable_captcha}
                      onChange={handleFormInputChange}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Embed HTML Section - Full Width */}
            <Card className='mt-3'>
              <Card.Header className='layout-card-header'>
                <h6 className='mb-0'>
                  <FaCopy className='me-2' size={16} />
                  Embed HTML
                </h6>
              </Card.Header>
              <Card.Body>
                <p className='text-muted small mb-3'>
                  Copy this HTML form and embed it on any website. The form includes your backend API URL
                  and company ID for submissions. No manual configuration needed!
                </p>
                <div className='d-flex gap-2 flex-wrap mb-3'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    disabled={!selectedForm?.id || embedLoading}
                    onClick={() => handleFetchEmbedHtml(false)}
                    title='Load the HTML currently stored in database'
                  >
                    {embedLoading && (
                      <Spinner as='span' animation='border' size='sm' role='status' className='me-2' />
                    )}
                    <FaSync className='me-1' />
                    Load Saved HTML
                  </Button>
                  <Button
                    variant='outline-primary'
                    size='sm'
                    disabled={!selectedForm?.id || embedLoading}
                    onClick={() => handleFetchEmbedHtml(true)}
                    title='Save current form configuration and regenerate HTML'
                  >
                    {embedLoading && (
                      <Spinner as='span' animation='border' size='sm' role='status' className='me-2' />
                    )}
                    <FaSync className='me-1' />
                    Save & Regenerate HTML
                  </Button>
                  <Button
                    variant='outline-success'
                    size='sm'
                    onClick={() => {
                      if (!formData.generated_html) {
                        handleFetchEmbedHtml(true).then(() => setShowPreview(true));
                      } else {
                        setShowPreview(true);
                      }
                    }}
                    disabled={embedLoading}
                    title='Preview the current HTML'
                  >
                    Preview
                  </Button>
                </div>
                {!selectedForm?.id ? (
                  <p className='text-muted small'>
                    Save the form first to generate a default embed snippet with the submission token.
                  </p>
                ) : (
                  <p className='text-muted small'>
                    <strong>Note:</strong> "Save & Regenerate HTML" will save your current configuration and generate updated HTML.
                  </p>
                )}
                <Form.Group controlId='formGeneratedHtml'>
                  <Form.Control
                    as='textarea'
                    rows={8}
                    placeholder='Embed HTML will appear here...'
                    value={formData.generated_html}
                    onChange={(event) =>
                      setFormData(prev => ({ ...prev, generated_html: event.target.value }))
                    }
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCloseModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSaveForm} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size='lg'
        centered
        dialogClassName='web-form-preview-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>Embed Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formData.generated_html ? (
            <iframe
              title='web-form-preview'
              className='web-form-preview-iframe'
              srcDoc={formData.generated_html}
            />
          ) : (
            <div className='text-muted text-center py-4'>
              Generate or paste embed HTML to preview the form.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WebFormManager;
